import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Typography, withStyles, Theme, Tooltip, Hidden } from '@material-ui/core';
import { AssetProps } from 'components/AssetListView/type';
import CodeVO from 'commons/vo/CodeVO';
import { RootState } from 'redux/reducers/type';
import { useStyles } from 'components/AssetListView/styles';
import { eventPropagationPrevent } from 'commons/utils/commonUtil';
import { useAsset, useLibrary } from 'hooks';
import CategoryVO from 'commons/vo/CategoryVO';
import StorageVO from 'commons/vo/StorageVO';
import { File, Resolution } from 'commons/constants/KeyFlowConstants';
import TimeCode from 'commons/utils/TimeCode';
import LibraryParamVO from 'commons/vo/param/LibraryParamVO';
import DownloadAssetSplitButton from 'components/DownloadAssetSplitButton';
import { getDayOfWeek } from 'commons/utils/DateUtils';

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(14),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

function Asset({ asset, index }: AssetProps) {
  const classes = useStyles();

  const { onRequestAssetDownloadCloudfront } = useAsset();
  const { onInsertLibrary } = useLibrary();
  const { pagination, searchCondition } = useSelector((state: RootState) => state.wkblVideo);
  const { storageList } = useSelector((state: RootState) => state.storage);
  const { categoryList } = useSelector((state: RootState) => state.category);
  const {
    wkblGameTypeList,
    kfScriptRefRangeList: rangeCodeList,
    wkblSeasonList,
  } = useSelector((state: RootState) => state.code);

  const [inTimecodeUtil, setInTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.inPoint);
    return timecodeUtil;
  });

  const [outTimecodeUtil, setOutTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.outPoint);
    return timecodeUtil;
  });

  const [durationTimecodeUtil] = useState<TimeCode>(() => {
    const timecodeUtil = new TimeCode(asset.mediaFps);
    timecodeUtil.setFrames(asset.outPoint - asset.inPoint);
    return timecodeUtil;
  });

  const storage: StorageVO | null = useMemo(() => {
    let result = null;
    const category = categoryList.filter(({ id }: CategoryVO) => id === asset.categoryId)[0];
    if (category) {
      result = storageList.filter(({ id }: StorageVO) => id === category.storageId)[0];
    }
    return result;
  }, [categoryList, storageList, asset.categoryId]);

  const symbolicPath: string = useMemo(() => {
    if (storage) {
      return storage.symbolicPath;
    }
    return '';
  }, [storage]);

  const seasonName = useMemo(() => {
    const seasonItem: CodeVO[] =
      wkblSeasonList.filter((item: CodeVO) => item.id === Number(asset.season)) || [];
    if (seasonItem.length > 0) {
      return seasonItem[0].name || '없음';
    }
    return '없음';
  }, [wkblSeasonList, asset.season]);

  const assetNo = useMemo(() => {
    const { pageNumber, returnCount } = pagination;
    const returnCnt = returnCount || searchCondition.retr;
    return returnCnt * pageNumber + index + 1;
  }, [pagination, index]);

  const rangeCodeName = useMemo(() => {
    const rangeCode: CodeVO[] =
      rangeCodeList.filter((item: CodeVO) => item.codevalue === String(asset.subType)) || [];
    if (rangeCode.length > 0) {
      return rangeCode[0].name || '';
    }
    return '';
  }, [rangeCodeList, asset]);

  const gameTypeName = useMemo(() => {
    const gameTypeItem: CodeVO[] =
      wkblGameTypeList.filter((item: CodeVO) => item.id === Number(asset.gameType)) || [];
    if (gameTypeItem.length > 0) {
      return gameTypeItem[0].name || '없음';
    }
    return '없음';
  }, [wkblGameTypeList, asset.gameType]);

  const team1LogoPath = useMemo(() => {
    return asset.getTeam1LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const team2LogoPath = useMemo(() => {
    return asset.getTeam2LogoPath(symbolicPath);
  }, [asset, symbolicPath]);

  const onPopupAssetDetail = useCallback(
    (event: React.MouseEvent) => {
      const { refId, id, title, subType } = asset;
      const inTime = inTimecodeUtil.getSeconds();
      const outTime = outTimecodeUtil.getSeconds();
      const encodedKeyword = encodeURIComponent(subType);

      let redirectUrl = `/preview/${id}`;
      if (refId !== id && rangeCodeName) {
        // redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&keyword=${encodedKeyword}`;
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}&subType=${encodedKeyword}`;
      } else if (refId !== id && !rangeCodeName) {
        redirectUrl = `/preview/${refId}?in=${inTime}&out=${outTime}`;
      }
      window.open(redirectUrl);
    },
    [asset, inTimecodeUtil, outTimecodeUtil, rangeCodeName],
  );

  const onEventPrevent = useCallback((event: React.MouseEvent) => {
    eventPropagationPrevent(event);
  }, []);

  const onDownloadLowres = useCallback(() => {
    const { categoryId, lowresPath, title, formattedGameDate } = asset;
    const gameDate = formattedGameDate.replace(/-/g, '');
    onRequestAssetDownloadCloudfront({
      categoryId,
      fileName: lowresPath,
      downloadFileName: `${gameDate}_${title}.mp4`,
    });

    // TODO - lowresPath가 안나오는 경우도 있음(기존 서비스에서는 에러나지 않는 부분)
    // onRequestAssetDownloadCloudfront({
    //   categoryId,
    //   fileName: lowresPath`,
    // });
  }, []);

  const { team1, team2 } = useMemo(() => {
    return asset.getTeamLogoPath();
  }, [asset]);

  return (
    <div className={clsx(classes.rowContainer, classes.row)} onClick={onPopupAssetDetail}>
      <Hidden mdDown>
        <span className={clsx(classes.headerColumn, classes.seasonContainer)}>{seasonName}</span>
        <span className={clsx(classes.headerColumn, classes.typeContainer)}>
          <div>{gameTypeName}</div>
        </span>
        <span
          className={clsx(classes.headerColumn, classes.titleContainer, classes.alignLeftColumn)}
        >
          <div>{asset.title}</div>
        </span>
        {/* 홈 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{asset.team1Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {asset.team1 && team1.db && asset.team1Name && (
              <div>
                <img src={team1.db}></img>
                <span>{asset.team1Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        {/* 어웨이 팀 */}
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography>{asset.team2Name}</Typography>
            </React.Fragment>
          }
        >
          <span className={clsx(classes.headerColumn, classes.teamContainer)}>
            {asset.team2 && team2.db && asset.team2Name && (
              <div>
                <img src={team2.db}></img>
                <span>{asset.team2Name}</span>
              </div>
            )}
          </span>
        </HtmlTooltip>
        <span className={clsx(classes.headerColumn, classes.gameDateContainer)}>
          <div>
            {asset.formattedGameDate} ({getDayOfWeek(asset.formattedGameDate)})
          </div>
        </span>
        <span
          className={clsx(classes.headerColumn, classes.menuContainer)}
          onClick={onEventPrevent}
        >
          <DownloadAssetSplitButton
            asset={asset}
            onDownloadClick={onDownloadLowres}
            isListView={true}
          />
        </span>
      </Hidden>
      <Hidden lgUp>
        <div className={clsx(classes.headerColumn, classes.verticalColumn)}>
          <span>{seasonName}</span>
          <span>
            <div>{gameTypeName}</div>
          </span>
          <span>
            <div className={classes.textEllipsis}>{asset.title}</div>
          </span>
          <span className={clsx(classes.gameDateContainer)}>
            <div>{asset.formattedGameDate}</div>
          </span>
        </div>
        <span
          className={clsx(classes.headerColumn, classes.menuContainer)}
          onClick={onEventPrevent}
        >
          <DownloadAssetSplitButton
            asset={asset}
            onDownloadClick={onDownloadLowres}
            isListView={true}
          />
        </span>
      </Hidden>
    </div>
  );
}

export default Asset;
